import type { RouteMeta, RouteRecordRaw } from 'vue-router'

import { keys } from '@/app/i18n'
import type { BreadcrumbItem } from '@/app/router/meta'

/**
 * The quotes module's routes (WP-09, wp/quotes §3): the five manifest rows of the quotes, paths and names from it
 * (D-15); the legacy paths (`/ac/quotes/create`, `/ac/quotes/edit/:id`, `/ac/quote/preview/:id`, `/quote/:id/h/:hash`)
 * reach them through the generated legacy redirects. The vendor pages are the `vendors` module's (OPEN-12 (b)).
 *
 * FX-058: legacy `meta.module` (`invoices`, `bank` on the public page) was never evaluated. The guard checks `resource`
 * (D-21): every employer route takes Invoicing — the permission every quote call needs (QUOTES Task 2) — and write on
 * the editor. The customer's page is public (`meta.public`, full layout) and calls the API without `Auth-Key`; its
 * title is "Your quote" (OPEN-5 (a), NEW-18; legacy "Your Invoice").
 */
const crumb = (text: string, to?: string): BreadcrumbItem => (to ? { text, to } : { text, active: true })

const quotes = crumb(keys.t('quotes.route.title.quotes'), '/ac/quotes')

const QuoteListView = () => import('./views/QuoteListView.vue')
const QuoteEditView = () => import('./views/QuoteEditView.vue')
const QuotePreviewView = () => import('./views/QuotePreviewView.vue')
const QuotePublicView = () => import('./views/QuotePublicView.vue')

const editorMeta = (title: string): RouteMeta => ({
  software: 'ac',
  resource: 'invoices',
  action: 'write',
  navActiveLink: 'quotes',
  pageTitle: title,
  breadcrumb: [quotes, crumb(title)],
})

const routes: RouteRecordRaw[] = [
  {
    path: '/ac/quotes',
    name: 'quotes',
    component: QuoteListView,
    meta: {
      software: 'ac',
      resource: 'invoices',
      navActiveLink: 'quotes',
      pageTitle: keys.t('quotes.route.title.quotes'),
      breadcrumb: [crumb(keys.t('quotes.route.title.quotes'))],
    },
  },
  {
    path: '/ac/quotes/new',
    name: 'quote-create',
    component: QuoteEditView,
    meta: editorMeta(keys.t('quotes.route.title.createQuote')),
  },
  {
    path: '/ac/quotes/:id(\\d+)/edit',
    name: 'quote-edit',
    component: QuoteEditView,
    meta: editorMeta(keys.t('quotes.route.title.editQuote')),
  },
  {
    path: '/ac/quotes/:id(\\d+)',
    name: 'quote-preview',
    component: QuotePreviewView,
    meta: {
      software: 'ac',
      resource: 'invoices',
      navActiveLink: 'quotes',
      pageTitle: keys.t('quotes.route.title.quotePreview'),
      breadcrumb: [quotes, crumb(keys.t('quotes.route.title.previewQuote'))],
    },
  },
  {
    path: '/quotes/:id(\\d+)/view/:hash',
    name: 'quote-public',
    component: QuotePublicView,
    meta: { layout: 'full', public: true, pageTitle: keys.t('quotes.route.title.yourQuote') },
  },
]

export default routes
